import React from 'react';
import { Building2, Target, CheckCircle2, Users } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const About = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header Section */}
      <section className="bg-gradient-to-br from-gray-50 to-gray-100 py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              About <span className="text-[#FF6B7A]">Sparecraft Solutions</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-700">
              Your reliable partner for JCB and forklift spare parts in Maharashtra
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Who We Are */}
            <div className="mb-16">
              <div className="flex items-center mb-6">
                <div className="bg-gradient-to-br from-[#00C9FF]/10 to-[#FF6B7A]/10 p-3 rounded-lg mr-4">
                  <Building2 className="h-8 w-8 text-[#00C9FF]" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900">Who We Are</h2>
              </div>
              <div className="prose prose-lg max-w-none text-gray-700">
                <p className="mb-4">
                  Sparecraft Solutions is a specialized trading company focused exclusively on <strong>JCB backhoe loader spare parts</strong> and <strong>forklift spare parts</strong>. We serve both retail customers who need individual parts and wholesale buyers including workshops, dealers, and fleet operators.
                </p>
                <p className="mb-4">
                  Based in <strong>Nagpur</strong>, we serve customers across Maharashtra with a special focus on the Vidarbha region. Our business is built on a simple principle: when your machine is down, you need the right part fast — not approximations, not delays.
                </p>
              </div>
            </div>

            {/* What Sets Us Apart */}
            <div className="mb-16">
              <div className="flex items-center mb-6">
                <div className="bg-gradient-to-br from-[#FF6B7A]/10 to-[#00C9FF]/10 p-3 rounded-lg mr-4">
                  <Target className="h-8 w-8 text-[#FF6B7A]" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900">What Sets Us Apart</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card className="border-2">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-3 flex items-center">
                      <CheckCircle2 className="h-5 w-5 text-[#00C9FF] mr-2" />
                      Specialized Focus
                    </h3>
                    <p className="text-gray-600">
                      We deal exclusively in JCB and forklift parts. This specialization means we understand compatibility, availability, and application better than general spare parts dealers.
                    </p>
                  </CardContent>
                </Card>
                <Card className="border-2">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-3 flex items-center">
                      <CheckCircle2 className="h-5 w-5 text-[#00C9FF] mr-2" />
                      Correct Identification
                    </h3>
                    <p className="text-gray-600">
                      We help identify the exact part you need based on your machine model and requirement. No guesswork, no wrong parts.
                    </p>
                  </CardContent>
                </Card>
                <Card className="border-2">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-3 flex items-center">
                      <CheckCircle2 className="h-5 w-5 text-[#00C9FF] mr-2" />
                      Stock Availability
                    </h3>
                    <p className="text-gray-600">
                      We maintain inventory of commonly needed parts. Enquiry-first approach ensures you know availability before placing orders.
                    </p>
                  </CardContent>
                </Card>
                <Card className="border-2">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-3 flex items-center">
                      <CheckCircle2 className="h-5 w-5 text-[#00C9FF] mr-2" />
                      Genuine & Aftermarket
                    </h3>
                    <p className="text-gray-600">
                      We supply genuine OEM parts as well as reliable OEM-equivalent aftermarket options, giving you choice based on your budget and requirements.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Who We Serve */}
            <div className="mb-16">
              <div className="flex items-center mb-6">
                <div className="bg-gradient-to-br from-[#00C9FF]/10 to-[#FF6B7A]/10 p-3 rounded-lg mr-4">
                  <Users className="h-8 w-8 text-[#FF6B7A]" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900">Who We Serve</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                  { title: 'Equipment Owners', desc: 'Individual JCB and forklift owners who need reliable parts' },
                  { title: 'Contractors', desc: 'Construction and logistics contractors running equipment fleets' },
                  { title: 'Workshops', desc: 'Service centers and repair workshops requiring consistent supply' },
                  { title: 'Fleet Operators', desc: 'Companies managing multiple machines and material handling equipment' },
                  { title: 'Dealers', desc: 'Authorized and independent dealers looking for wholesale supply' },
                  { title: 'Industrial Units', desc: 'Factories and warehouses with in-house forklifts' }
                ].map((customer, index) => (
                  <Card key={index} className="border-2 hover:border-[#00C9FF] transition-all">
                    <CardContent className="p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">{customer.title}</h3>
                      <p className="text-sm text-gray-600">{customer.desc}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Our Commitment */}
            <div className="bg-gradient-to-br from-[#FF6B7A]/5 to-[#00C9FF]/5 p-8 md:p-12 rounded-2xl border-2 border-[#00C9FF]/20">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">Our Commitment</h2>
              <div className="prose prose-lg max-w-none text-gray-700">
                <p className="mb-4">
                  At Sparecraft Solutions, we're committed to being more than just a parts supplier. We aim to be a <strong>reliable partner</strong> you can count on when your equipment needs attention.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <CheckCircle2 className="h-6 w-6 text-[#00C9FF] mr-3 flex-shrink-0 mt-0.5" />
                    <span><strong>Quick Response:</strong> Fast enquiry handling via WhatsApp and phone</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-6 w-6 text-[#00C9FF] mr-3 flex-shrink-0 mt-0.5" />
                    <span><strong>Transparent Communication:</strong> Clear information on availability, pricing, and delivery</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-6 w-6 text-[#00C9FF] mr-3 flex-shrink-0 mt-0.5" />
                    <span><strong>Quality Focus:</strong> Only reliable genuine and aftermarket parts</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-6 w-6 text-[#00C9FF] mr-3 flex-shrink-0 mt-0.5" />
                    <span><strong>Customer Support:</strong> Assistance in identifying the correct parts for your specific models</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
